import { ChevronDown } from 'lucide-react';
import { useRef, useEffect } from 'react';

interface InitTimeSelectorProps {
  availableInitTimes?: string[];
  disabled: boolean;
  isOpen: boolean;
  loading: boolean;
  selectedInitTime: string;
  onSelect: (initTime: string) => void;
  onToggle: () => void;
}

// Helper function to format datetime strings nicely
const formatInitTime = (initTime: string): string => {
  if (initTime === 'live') return 'Live';

  try {
    const date = new Date(initTime);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
  } catch {
    // If parsing fails, return the original string
    return initTime;
  }
};

export function InitTimeSelector({
  availableInitTimes,
  disabled,
  isOpen,
  loading,
  selectedInitTime,
  onSelect,
  onToggle,
}: InitTimeSelectorProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Create options with "Live" as default, excluding the first init time since it's represented by "Live"
  const liveOptionLabel =
    availableInitTimes && availableInitTimes.length > 0
      ? `Live (${formatInitTime(availableInitTimes[0])})`
      : 'Live';

  const options = [
    { id: 'live', label: liveOptionLabel },
    ...(availableInitTimes || [])
      .slice(1)
      .map((time) => ({ id: time, label: formatInitTime(time) })),
  ];

  // Find the selected option, or create a temporary option if not found
  const selectedOption = options.find((opt) => opt.id === selectedInitTime) || {
    id: selectedInitTime,
    label: formatInitTime(selectedInitTime),
  };

  return (
    <div className="relative mt-2" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
        Initialization Time (UTC)
      </label>
      <button
        onClick={() => onToggle()}
        disabled={disabled || loading}
        className={`
          w-full px-2 py-1 text-left bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 
          rounded text-xs focus:outline-none focus:ring-1 focus:ring-pink-500/20 focus:border-pink-300 
          disabled:opacity-50 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500
          transition-colors
        `}
      >
        <div className="flex items-center justify-between">
          <span className="block truncate">{loading ? 'Loading...' : selectedOption.label}</span>
          <ChevronDown
            className={`h-3 w-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && !loading && (
        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg max-h-40 overflow-auto">
          <div className="p-1">
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => onSelect(option.id)}
                className={`
                    w-full px-2 py-1 text-left text-xs rounded transition-colors
                    ${
                      selectedInitTime === option.id
                        ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-300'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }
                  `}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
